import type { ReactElement } from "react";
import type {
  DOMConversionMap,
  DOMExportOutput,
  EditorConfig,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from "lexical";
import { DecoratorNode } from "lexical";

export type SerializedImageNode = Spread<{ src: string; alt: string }, SerializedLexicalNode>;

export class ImageNode extends DecoratorNode<ReactElement> {
  __src: string;
  __alt: string;

  static getType(): string {
    return "image";
  }

  static clone(node: ImageNode): ImageNode {
    return new ImageNode(node.__src, node.__alt, node.__key);
  }

  constructor(src: string, alt = "", key?: NodeKey) {
    super(key);
    this.__src = src;
    this.__alt = alt;
  }

  createDOM(_config: EditorConfig): HTMLElement {
    const span = document.createElement("span");
    span.className = "ticket-editor-image";
    return span;
  }

  updateDOM(): false {
    return false;
  }

  isInline(): boolean {
    return false;
  }

  decorate(_editor: LexicalEditor, _config: EditorConfig): ReactElement {
    return (
      <img
        src={this.__src}
        alt={this.__alt}
        style={{ maxWidth: "100%", borderRadius: 8, display: "block", margin: "8px 0" }}
      />
    );
  }

  exportJSON(_compact?: boolean): SerializedImageNode {
    return { type: "image", version: 1, src: this.__src, alt: this.__alt };
  }

  static importJSON(serialized: SerializedImageNode): ImageNode {
    return $createImageNode(serialized.src || "", serialized.alt || "");
  }

  exportDOM(): DOMExportOutput {
    const img = document.createElement("img");
    img.setAttribute("src", this.__src);
    img.setAttribute("alt", this.__alt);
    img.style.maxWidth = "100%";
    return { element: img };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      img: () => ({
        conversion: (element) => {
          if (!(element instanceof HTMLImageElement) || !element.src) {
            return null;
          }
          return { node: $createImageNode(element.src, element.alt) };
        },
        priority: 1,
      }),
    };
  }
}

export function $createImageNode(src: string, alt = ""): ImageNode {
  return new ImageNode(src, alt);
}

export function $isImageNode(node: LexicalNode | null | undefined): node is ImageNode {
  return node instanceof ImageNode;
}
